import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule, CdkDragDrop, transferArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { ProjectStage } from '../../../shared/models/project.model';
import { Task } from '../../../shared/models/task.model';
import { TaskService } from '../../../shared/services/task.service';
import { UserService } from '../../../shared/services/user.service';
import { User as UserModel } from '../../../shared/models/user.model';
import { FormsModule } from '@angular/forms';
import { User } from '../../../shared/services/user';

@Component({
  selector: 'app-kanban-stage-project',
  imports: [CommonModule, DragDropModule, FormsModule],
  templateUrl: './kanban-stage-project.html',
  styleUrl: "kanban-stage-project.scss",
})
export class KanbanStageProjectComponent implements OnInit {
  private user = inject(User);
  private userService = inject(UserService);
  private taskService = inject(TaskService);

  @Input() projectStages: ProjectStage[] = [];
  @Input() allTasks: Task[] = [];
  @Input() tasksByStage: Record<string, Task[]> = {};
  @Input() projectManagerUserIds: string[] = []; // User IDs of project managers
  @Input() encodedHoursByStage: Record<string, number> = {};
  @Output() taskMoved = new EventEmitter<{ taskId: string; toStageId: string }>();
  @Output() addTask = new EventEmitter<string>();
  /** The parent owns the projectId, so it performs the navigation to the task page. */
  @Output() taskDetailOpen = new EventEmitter<string>();
  @Output() stageChange = new EventEmitter<{ stageId: string, changes: Partial<ProjectStage> }>();
  employees: UserModel[] = [];

  /** Archived members are hidden from the member filter by default. */
  showArchivedEmployees = false;

  /**
   * Members offered in the filter dropdown. `employees` keeps everybody so
   * tasks assigned to an archived member still show their name and avatar.
   */
  get visibleEmployees(): UserModel[] {
    return this.employees.filter(emp =>
      !emp.isArchived ||
      this.showArchivedEmployees ||
      (!!this.filterEmployeeId && emp.id === this.filterEmployeeId)
    );
  }

  get archivedEmployeeCount(): number {
    return this.employees.filter(emp => !!emp.isArchived).length;
  }

  currentUserId: string | null = null;


  filterEmployeeId: string | null = "";
  filterStatus: string | null = "";
  filterPriority: string | null = "";

  ngOnInit() {
    this.userService.users$.subscribe(employees => {this.employees = employees;});
    this.userService.currentUser$.subscribe(user => {
      this.currentUserId = user?.id || null;
    });
  }

  get stageIds(): string[] {
    return this.projectStages.map(s => s.projectStageId!).filter(id => !!id) as string[];
  }

  /**
   * A card is both draggable and clickable, so the click that ends a drag has to
   * be swallowed — otherwise dropping a card also opened it.
   */
  private draggingTaskId: string | null = null;
  private dragEndedAt = 0;

  onDragStarted(task: Task) {
    this.draggingTaskId = task.id ?? null;
  }

  onDragEnded() {
    this.draggingTaskId = null;
    this.dragEndedAt = Date.now();
  }

  async onDrop(event: CdkDragDrop<Task[]>, toStageId: string) {
    // Resolved from the drag payload, not from previousIndex: CDK indexes the
    // rendered cards, while the container data still holds the filtered-out
    // tasks — so an index lookup moved the wrong task whenever a filter was on.
    const task = event.item.data as Task | undefined;
    if (!task?.id) return;

    if (!this.canUserEditTask(task)) {
      console.warn('User does not have permission to move this task');
      return;
    }

    const from = event.previousContainer.data;
    const to = event.container.data;
    const fromIndex = from.indexOf(task);
    if (fromIndex === -1) return;

    const sameColumn = event.previousContainer === event.container;
    const toIndex = this.dataIndexFor(to, event.currentIndex);

    if (sameColumn) {
      if (fromIndex === toIndex) return;
      moveItemInArray(to, fromIndex, toIndex);
    } else {
      transferArrayItem(from, to, fromIndex, toIndex);
      this.taskMoved.emit({ taskId: task.id, toStageId });
    }

    // Persist the new order of the column the card landed in, the same way the
    // list view does — a drop used to be forgotten as soon as the data reloaded.
    await this.taskService.reorderTasks(to);
  }

  /**
   * Translate an index over the *rendered* cards into an index into the column's
   * data array, which also contains the tasks the filters hide.
   *
   * The dragged card stays in `rendered`: within one column CDK's currentIndex is
   * the position the card ends up at among the cards as drawn, so dropping it
   * means taking the slot currently held by `rendered[currentIndex]`. Leaving the
   * card out shifted every downward move one place too far. Across columns the
   * card is not in the target list anyway, so the same lookup gives the insertion
   * point unchanged.
   */
  private dataIndexFor(list: Task[], renderedIndex: number): number {
    const rendered = list.filter(t => this.filterTask(t));
    if (renderedIndex >= rendered.length) return list.length;
    const target = list.indexOf(rendered[renderedIndex]);
    return target === -1 ? list.length : target;
  }

  onAddTask(stageId: string) {
    this.addTask.emit(stageId);
  }

  openDetailTask(taskId: string) {
    // Ignore the click the browser fires right after a drag finishes.
    if (this.draggingTaskId || Date.now() - this.dragEndedAt < 200) return;
    this.taskDetailOpen.emit(taskId);
  }

  // Helper method to find task by ID
  private findTask(taskId: string): Task | null {
    for (const stageId of Object.keys(this.tasksByStage)) {
      const task = this.tasksByStage[stageId]?.find(t => t.id === taskId);
      if (task) return task;
    }
    return null;
  }

  // Check if user can edit this task
  // Allow all users to edit tasks
  canUserEditTask(task: Task): boolean {
    return true;
  }


  getTaskEmployees(task: Task): UserModel[] {
    if (!task.employeeId || !Array.isArray(task.employeeId)) return [];
    return this.employees.filter(emp => emp.id && task.employeeId?.includes(emp.id));
  }

  getEmployeeNames(employees: UserModel[]): string {
    return employees.map(e => e.firstName + ' ' + e.name).join(', ');
  }

  getInitials(emp: UserModel): string {
    const first = (emp.firstName || '').charAt(0).toUpperCase();
    const last = (emp.name || '').charAt(0).toUpperCase();
    return (first + last) || '?';
  }

  getStageEncodedHours(stageId: string): number {
    return this.encodedHoursByStage[stageId] || 0;
  }

  filterTask(task: Task): boolean {
    return (!this.filterEmployeeId || !!task.employeeId?.includes(this.filterEmployeeId))
        && (!this.filterStatus || task.status === this.filterStatus)
        && (!this.filterPriority || task.priority === this.filterPriority);
  }
  onStageFieldChange(stageId: string, fieldName: keyof ProjectStage, value: any) {
    this.stageChange.emit({
      stageId,
      changes: { [fieldName]: value }
    });
  }

  updateTaskField(taskId: string, fieldName: string, value: any) {
    this.taskService.updateTask(taskId, { [fieldName]: value })
      .then(() => console.log('Task updated'))
      .catch(err => console.error('Error updating task', err));
  }

  getStageRemainingTime(stageId: string): number {
    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    if (!stage) return 0;
    const stageEst = stage.estimatedHours || 0;
    const tasksForStage = this.allTasks.filter(t => t.projectStageId === stageId);
    const tasksSum = tasksForStage.reduce((acc, task) => acc + (task.estimatedHours || 0), 0);
    return stageEst - tasksSum;
  }
}
